import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import '../styles/boat/BoatPage.css';
import { validateBoatLeadForm } from '../validations/BoatLeadValidation';
import bannerImage from '../../images/BoatPage/TravelWebKitBase_BoatLeadBanner.png';
import CompanyTripAdvisorReviewsSection from '../components/HomePage/CompanyTripAdvisorReviewsSection';
import FAQ from '../components/HomePage/FAQ';
import WebPreloader from '../components/Common/WebPreloader';
import SEOHead from '../components/Common/SEOHead';
import BoatService from '../services/BoatService';
import BoatLeadService from '../services/BoatLeadService';
import RelatedBoats from '../components/BoatPage/RelatedBoats';
import BackToListingButton from '../components/BoatPage/BackToListingButton';
import BoatLeadSuccessModal from '../components/BoatsPage/BoatLeadSuccessModal';
import BoatReviews from '../components/BoatPage/BoatReviews';

import BoatPageGallery from '../components/BoatPage/BoatPageGallery';
import BoatDetailsTwoColSection from '../components/BoatPage/BoatDetailsTwoColSection';
import BoatFeatureBar from '../components/BoatPage/BoatFeatureBar';

const BoatPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [boat, setBoat] = useState(null);
  const [formData, setFormData] = useState({
    boat_id: parseInt(id, 10),
    fullName: '',
    email: '',
    phone: '',
    date: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPreloader, setShowPreloader] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const loadBoat = async () => {
      try {
        const data = await BoatService.fetchBoatById(id);
        setBoat(data);
        console.log('Boat data:', data);
      } catch (error) {
      }
    };

    loadBoat();

    const timer = setTimeout(() => setShowPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, [id]);

  // Handle scrolling to reviews section
  useEffect(() => {
    if (location.state?.scrollToReviews && boat) {
      // Wait for the page to load and then scroll to reviews
      const timer = setTimeout(() => {
        const reviewsSection = document.querySelector('.boat-reviews');
        if (reviewsSection) {
          reviewsSection.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
          
          // Clean up the navigation state after scrolling
          window.history.replaceState(
            { ...location.state, scrollToReviews: false },
            ''
          );
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [location.state?.scrollToReviews, boat, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setFormData({
      boat_id: parseInt(id, 10),
      fullName: '',
      email: '',
      phone: '',
      date: '',
      message: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateBoatLeadForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const newLead = {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        date: formData.date || null,
        message: formData.message || null,
        boat: formData.boat_id,
      };

      await BoatLeadService.createLead(newLead);
      setShowSuccessModal(true);
    } catch (error) {
      setErrors({ submit: 'Error sending the form. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!boat) {
    return <p>Loading...</p>;
  }

  const mainImage = boat.main_image;
  const galleryImages = boat.gallery_images && boat.gallery_images.length > 0
    ? boat.gallery_images
        .map(img => img.image)
        .filter(imgUrl => imgUrl !== mainImage)
    : [];
  const images = [mainImage, ...galleryImages].filter(Boolean);
  console.log('images for BoatPageGallery:', images);

  return (
    <div className="boat-page">
      <SEOHead boatId={parseInt(id, 10)} />
      <BackToListingButton boat={boat} />
      
      <div className="boatpage-gallery-wrapper">
        <BoatPageGallery images={images} />
      </div>
      <BoatDetailsTwoColSection
        boat={boat}
        formData={formData}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        // Insertamos el feature bar para mobile justo después del note
        extraBelowNote={<div className="boat-feature-bar mobile-only"><BoatFeatureBar /></div>}
      />
      <div className="boat-page__wave-container">
        <svg className="waves" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
        viewBox="0 24 150 28" preserveAspectRatio="none" shapeRendering="auto">
          <defs>
            <path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
          </defs>
          <g className="parallax">
            <use xlinkHref="#gentle-wave" x="48" y="0" fill="rgba(0, 96, 152, 0.2)" />
            <use xlinkHref="#gentle-wave" x="48" y="3" fill="rgba(0, 96, 152, 0.4)" />
            <use xlinkHref="#gentle-wave" x="48" y="5" fill="rgba(0, 96, 152, 0.6)" />
            <use xlinkHref="#gentle-wave" x="48" y="7" fill="#006098" />
          </g>
        </svg>
      </div>
      {/* Desktop only feature bar */}
      <div className="boat-feature-bar desktop-only">
        <BoatFeatureBar />
      </div>
      <BoatReviews boatId={boat.id} boatName={boat.name} />
      <RelatedBoats boat={boat} />
      
      <CompanyTripAdvisorReviewsSection />
      <FAQ />
      {showSuccessModal && <BoatLeadSuccessModal onClose={handleSuccessModalClose} />}
      {showPreloader && <WebPreloader />}
    </div>
  );
};

export default BoatPage;